import React, { useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import { fontSizePalette } from 'utils/styleConstants';
import Dropdown from 'components/Input/dropdown/Dropdown';
import Icon from 'components/Input/Icon';
import NumberInput from 'components/Input/NumberInput';

const IconWrapper = styled.div<{ disabled: boolean }>`
  display: inline-flex;
  align-items: center;
  margin: 0 5px;
`;

const StyledPagination = styled.div<{ marginTop: number }>`
  width: 450px;
  margin-top: ${({ marginTop }) => marginTop}px;
  padding: 16px 0;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 16px;
  font-size: ${fontSizePalette.xSmall};
  color: ${({ theme }) => theme.colorPalette.grey.grey9};
`;

const Span = styled.span`
  word-wrap: normal;
  white-space: nowrap;
`;

const RowsPerPageWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PaginationControls = styled.div`
  margin-left: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
`;

interface PaginationProps {
  marginTop?: number;
  pageIndex: number;
  pageCount: number;
  pageOptions: number[];
  nextPage: () => void;
  canPreviousPage: boolean;
  canNextPage: boolean;
  gotoPage: (page: number) => void;
  previousPage: () => void;
  pageSize: number;
  onPageSizeChange: (size: number) => void;
}

const Pagination = (props: PaginationProps) => {
  const {
    gotoPage,
    canPreviousPage,
    canNextPage,
    nextPage,
    pageCount,
    // pageOptions,
    pageIndex,
    marginTop = 0,
    previousPage,
    pageSize,
    onPageSizeChange,
  } = props;
  const [inputValue, setInputValue] = useState<string>(String(pageIndex + 1));

  useEffect(() => {
    if (pageIndex >= pageCount) {
      gotoPage(Math.max(0, pageCount - 1));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageCount]);

  useEffect(() => {
    setInputValue(String(pageIndex + 1));
  }, [pageIndex]);

  const handlePageInputChange = (value: number | undefined) => {
    setInputValue(value !== undefined ? String(value) : '');
  };

  const handlePageInputBlur = () => {
    const pageNum = parseInt(inputValue, 10);
    if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= pageCount) {
      gotoPage(pageNum - 1);
    } else {
      setInputValue(String(pageIndex + 1));
    }
  };
  const closeMenuOnScroll = (event: any) => {
    if (event.target instanceof Document) {
      const activeElement = event.target.activeElement;
      if (activeElement?.id?.includes('react-select')) return false;
      return true;
    }
    if (event.target instanceof HTMLElement) {
      if (event.target?.className?.includes('MenuList') || event.target.parentElement?.className?.includes('menu')) {
        return false;
      }
      return true;
    }
    return true;
  };

  const rowsPerPageOptions = Array.from(new Set([pageSize, 10, 25, 50, 100]))
    .sort((a, b) => a - b)
    .map((value) => ({
      label: String(value),
      value,
    }));

  return (
    <StyledPagination className="table-pagination" marginTop={marginTop}>
      <RowsPerPageWrapper>
        <Span>Rows per page:</Span>
        <Dropdown
          id="rows-per-page-dropdown"
          value={{ label: String(pageSize), value: pageSize }}
          onChange={({ value }) => onPageSizeChange(Number(value))}
          options={rowsPerPageOptions}
          minWidthLabel="74px"
          overflowWidth="3px"
          closeMenuOnScroll={closeMenuOnScroll}
        />
      </RowsPerPageWrapper>

      <PaginationControls>
        <IconWrapper
          disabled={!canPreviousPage || pageCount === 1}
          onClick={() => !(!canPreviousPage || pageCount === 1) && gotoPage(0)}
        >
          <Icon icon="double-chevron" rotate={270} size="m" disabled={!canPreviousPage || pageCount === 1} />
        </IconWrapper>
        <IconWrapper
          disabled={!canPreviousPage || pageCount === 1}
          onClick={() => !(!canPreviousPage || pageCount === 1) && previousPage()}
        >
          <Icon icon="chevron" rotate={90} size="m" disabled={!canPreviousPage || pageCount === 1} />
        </IconWrapper>
        <Span>Page:</Span>
        <NumberInput value={inputValue} onChange={handlePageInputChange} onBlur={handlePageInputBlur} width={'60px'} />
        <Span>of {pageCount}</Span>
        <IconWrapper
          disabled={!canNextPage || pageCount === 1}
          onClick={() => !(!canNextPage || pageCount === 1) && nextPage()}
        >
          <Icon icon="chevron" size="m" rotate={270} disabled={!canNextPage || pageCount === 1} />
        </IconWrapper>
        <IconWrapper
          disabled={!canNextPage || pageCount === 1}
          onClick={() => !(!canNextPage || pageCount === 1) && gotoPage(pageCount - 1)}
        >
          <Icon icon="double-chevron" size="m" rotate={90} disabled={!canNextPage || pageCount === 1} />
        </IconWrapper>
      </PaginationControls>
    </StyledPagination>
  );
};

export default Pagination;
